import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  pickRandom, createCueSequencer, cueGapMs,
  speak, cancelSpeech, primeSpeech, bell, tick,
  estimateCalories,
} from "@/lib/roninBox";
import { useRoninPreview } from "@/lib/roninPreviewContext";

// Constant: 3-second breather between the bell and the very first cue of every round.
// Gives the user time to set their stance before the engine starts barking combos.
const FIRST_CUE_DELAY_MS = 3000;
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

/**
 * useRoninRunner — the workout state machine (bootstrap, audio preload, RAF timer loop,
 * cue firing, phase transitions, wake-lock, pause/skip/stop). Extracted from RoninBoxRunner
 * so the component is purely presentational. Behavior is unchanged.
 *
 * Returns render state + control callbacks. `activeMsRef` is returned for the live "Active"
 * stat (read during render without forcing a re-render every frame).
 */
export function useRoninRunner({ config, selectedCueIds, selectedLevel, onComplete }) {
  const { mode, rounds, roundSecs, restSecs, intensity, exCount = 0, exLen = 30 } = config;
  // Circuit mode reuses the SHADOW cue pool for its boxing segments.
  const cueMode = mode === "circuit" ? "shadow" : mode;

  // Loading state: fetch cues + audio before starting
  const [bootstrapped, setBootstrapped] = useState(false);
  const [bootError, setBootError] = useState(null);
  const cuesRef = useRef([]);
  const settingsRef = useRef(null);
  const audioCacheRef = useRef({}); // cueId -> HTMLAudioElement
  const currentAudioRef = useRef(null); // currently-playing element (to .pause() on next cue)

  // Phase: "countdown" | "round" | "rest" | "done"
  const [phase, setPhase] = useState("countdown");
  const [phaseSecs, setPhaseSecs] = useState(5); // pre-round 5-4-3-2-1
  const [roundIdx, setRoundIdx] = useState(0); // 0-based current round
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(false);
  const [cue, setCue] = useState("Loading…");
  const [punches, setPunches] = useState(0);
  // Active punch-cue animation (flip-book) + a key that bumps on each new cue so it restarts.
  const [activeAnim, setActiveAnim] = useState(null);
  const [animKey, setAnimKey] = useState(0);
  const animByCueRef = useRef({}); // cueId -> animation
  // Circuit mode — timed exercise breaks within a round.
  const [exercise, setExercise] = useState(null); // active exercise name, or null
  const exercisePoolRef = useRef([]); // active exercise names
  const exWindowsRef = useRef([]); // [{start,end,name}] ms relative to round start
  const inExerciseRef = useRef(false);

  // Refs that drive the engine (no re-render on tick)
  const cueSequencerRef = useRef(null);
  const atmospherePoolRef = useRef([]);
  const nextCueAtRef = useRef(0); // ms epoch
  const startedAtRef = useRef(Date.now());
  const activeMsRef = useRef(0); // accumulated active (round-phase) ms
  const lastFrameAtRef = useRef(Date.now());
  const phaseEndsAtRef = useRef(Date.now() + 5000);
  const phaseRef = useRef("countdown");
  const pausedRef = useRef(false);
  const mutedRef = useRef(false);
  const punchesRef = useRef(0);
  const lastCountdownTickRef = useRef(5);
  const pausedRemainingRef = useRef(null);
  const roundIdxRef = useRef(0); // mirrors `roundIdx` state so the RAF loop can read it
                                  // without forcing the engine effect to re-run + reset timer.
  const roundStartedAtRef = useRef(Date.now()); // when current round began — feeds sequencer.nextCue

  // Keep refs in sync with state (so the loop can read them)
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { pausedRef.current = paused; }, [paused]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { roundIdxRef.current = roundIdx; }, [roundIdx]);

  // Bootstrap — fetch cues + settings + preload audio
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cuesRes, settingsRes, sequencesRes] = await Promise.all([
          api.get("/ronin-box/cues"),
          api.get("/ronin-box/settings"),
          api.get("/ronin-box/sequences"),
        ]);
        if (cancelled) return;
        const rawCues = cuesRes.data || [];
        // Apply user's cue selection (from RoninCuePicker). Empty/null = all.
        const filtered = (selectedCueIds && selectedCueIds.length > 0)
          ? rawCues.filter((c) => selectedCueIds.includes(c.id))
          : rawCues;
        cuesRef.current = filtered;
        settingsRef.current = settingsRes.data || {};
        // Build the ratio-based sequencer. Per-mode ratios + range frequency come from settings
        // (admin-configurable on /admin/ronin-box settings panel); both fall back to sensible
        // defaults if unset.
        cueSequencerRef.current = createCueSequencer({
          cues: cuesRef.current, mode: cueMode, level: selectedLevel, selectedCueIds,
          ratios: settingsRef.current.mode_category_ratios,
          rangePerRound: settingsRef.current.mode_range_per_round?.[cueMode],
          sequences: sequencesRes.data || [],
        });
        // Atmosphere pool stays as a probabilistic pre-empt — the legacy behavior.
        atmospherePoolRef.current = (cuesRef.current || []).filter((c) => c.category === "atmosphere");
        // Cue animations (optional) — never block workout start if they fail to load.
        try {
          const animsRes = await api.get("/ronin-box/animations");
          const map = {};
          for (const a of animsRes.data || []) {
            for (const cid of a.cue_ids || []) if (!map[cid]) map[cid] = a;
          }
          animByCueRef.current = map;
        } catch { animByCueRef.current = {}; }
        // Circuit mode — load the active exercise pool for timed breaks.
        if (mode === "circuit") {
          try {
            const exRes = await api.get("/ronin-box/exercises");
            exercisePoolRef.current = (exRes.data || [])
              .filter((e) => e.name)
              .map((e) => ({ name: e.name, audioUrl: e.audio_url || null }));
          } catch { exercisePoolRef.current = []; }
        }
        // Fail loudly if there are no actionable cues — better than a silent dead workout.
        // We need at least ONE non-atmosphere cue or the sequencer has nothing to emit.
        const hasActionable = (cuesRef.current || []).some((c) => c.category !== "atmosphere");
        if (!hasActionable) {
          setBootError(
            `No cues selected for ${mode} mode. Go back and pick some cues from the library.`
          );
          return;
        }
        // Preload audio elements (kick off in parallel — don't block on load)
        const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        for (const c of cuesRef.current) {
          if (c.audio_url) {
            const a = new Audio(`${apiBase}${c.audio_url}`);
            a.preload = "auto";
            audioCacheRef.current[c.id] = a;
          }
        }
        primeSpeech();
        setBootstrapped(true);
        setCue("Get ready");
      } catch (err) {
        if (!cancelled) {
          const status = err?.response?.status;
          if (status === 404) {
            setBootError("Ronin Box backend isn't installed on this server. Tell the coach.");
          } else {
            setBootError("Couldn't load workout — check your connection and try again.");
          }
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Cleanup on unmount — cancel any in-flight speech
  useEffect(() => () => { cancelSpeech(); }, []);

  // Wake Lock — keep the phone screen ON while a workout is active. iOS Safari 16.4+ and
  // Android Chrome both implement `navigator.wakeLock.request('screen')`. The OS auto-releases
  // the lock when the tab loses focus, so we re-acquire on visibilitychange when we come back.
  // No-ops gracefully on browsers without Wake Lock support (the runner still works, just doesn't
  // keep the screen on — falling back to the user's normal display-off setting).
  const wakeLockRef = useRef(null);
  useEffect(() => {
    if (!bootstrapped) return;
    let cancelled = false;
    const acquire = async () => {
      try {
        if (!("wakeLock" in navigator)) return;
        if (cancelled) return;
        wakeLockRef.current = await navigator.wakeLock.request("screen");
        // If the system releases the lock (tab backgrounded, low battery, etc), null our ref
        // so visibilitychange can re-acquire cleanly on return.
        wakeLockRef.current.addEventListener("release", () => { wakeLockRef.current = null; });
      } catch {
        // Permission denied / not supported — silently skip, workout still runs
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible" && !wakeLockRef.current) acquire();
    };
    acquire();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (wakeLockRef.current) {
        try { wakeLockRef.current.release(); } catch { /* ignore */ }
        wakeLockRef.current = null;
      }
    };
  }, [bootstrapped]);

  // Helper — play a cue (audio file if cached, else Web Speech fallback)
  const playCueAudio = (cueObj) => {
    if (mutedRef.current) return;
    // Stop previous audio so cues don't overlap
    if (currentAudioRef.current) {
      try { currentAudioRef.current.pause(); currentAudioRef.current.currentTime = 0; } catch { /* ignore */ }
    }
    const cached = audioCacheRef.current[cueObj.id];
    if (cached) {
      try {
        // Defensive guard against silent ElevenLabs blips (e.g. bare digit cues
        // like "1" or "1-2" historically returned <200ms unplayable audio). If
        // the metadata says the file is unplayably short, ALSO fire the on-device
        // voice so the user still hears the cue. duration is NaN until metadata
        // loads, so we only fall back when we have a concrete sub-threshold value.
        const dur = cached.duration;
        const tooShort = Number.isFinite(dur) && dur > 0 && dur < 0.35;
        cached.currentTime = 0;
        const p = cached.play();
        if (p && p.catch) p.catch(() => speak(cueObj.text)); // fallback if play() rejected
        currentAudioRef.current = cached;
        if (tooShort) speak(cueObj.text);
        return;
      } catch { /* fall through to speak */ }
    }
    speak(cueObj.text);
  };

  // Optional admin-preview hook — when the runner is mounted inside the admin preview page,
  // we ping the context every time a cue fires so the admin can jump to the editor.
  // Null in normal user mode → zero overhead.
  const previewCtx = useRoninPreview();
  const previewCtxRef = useRef(previewCtx);
  useEffect(() => { previewCtxRef.current = previewCtx; }, [previewCtx]);

  // Build this round's exercise-break schedule (circuit mode). Windows are evenly spaced
  // and bookended by boxing. Resets circuit state; called at the start of every round.
  const buildExerciseWindows = () => {
    exWindowsRef.current = [];
    inExerciseRef.current = false;
    setExercise(null);
    if (mode !== "circuit") return;
    const pool = exercisePoolRef.current;
    if (!pool.length) return;
    let n = Math.max(0, Math.min(5, exCount));
    const L = Math.max(5, exLen);
    // Keep at least ~10s of boxing plus a gap between/around exercises; shrink count to fit.
    while (n > 0 && roundSecs - n * L < Math.max(10, L)) n -= 1;
    if (n <= 0) return;
    const gap = (roundSecs - n * L) / (n + 1);
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    const wins = [];
    for (let i = 0; i < n; i++) {
      const startSec = gap * (i + 1) + L * i;
      const ex = shuffled[i % shuffled.length];
      wins.push({ start: startSec * 1000, end: (startSec + L) * 1000, name: ex.name, audioUrl: ex.audioUrl });
    }
    exWindowsRef.current = wins;
  };

  // Each frame during a round: enter/exit the current exercise window, announce it, and
  // suppress boxing cues while an exercise is running.
  const updateCircuit = (now) => {
    const elapsed = now - roundStartedAtRef.current;
    const win = exWindowsRef.current.find((w) => elapsed >= w.start && elapsed < w.end);
    if (win && !inExerciseRef.current) {
      inExerciseRef.current = true;
      setExercise(win.name);
      setCue(win.name);
      setActiveAnim(null);
      if (!mutedRef.current) {
        cancelSpeech();
        if (win.audioUrl) {
          // Play the pre-generated George-voice mp3; fall back to TTS if it fails.
          try { const a = new Audio(`${API_BASE}${win.audioUrl}`); a.play().catch(() => speak(win.name, { rate: 1.0 })); }
          catch { speak(win.name, { rate: 1.0 }); }
        } else {
          speak(win.name, { rate: 1.0 });
        }
      }
      nextCueAtRef.current = Number.MAX_SAFE_INTEGER; // no cues during the exercise
    } else if (!win && inExerciseRef.current) {
      inExerciseRef.current = false;
      setExercise(null);
      setCue("Box!");
      if (!mutedRef.current) { cancelSpeech(); speak("Box", { rate: 1.0 }); }
      nextCueAtRef.current = now + 1200;
    }
  };

  const fireCue = () => {
    const seq = cueSequencerRef.current;
    if (!seq) return;
    const atmoChance = (settingsRef.current?.atmosphere_chance) ?? 0.07;
    const atmoPool = atmospherePoolRef.current || [];
    // Atmosphere pre-empts the sequencer probabilistically — keeps the original "Breathe / Stay loose" vibe.
    if (atmoPool.length > 0 && Math.random() < atmoChance) {
      const a = pickRandom(atmoPool);
      if (a) {
        setCue(a.text);
        setActiveAnim(null); // atmosphere cues have no combo animation
        playCueAudio(a);
        // Atmosphere cues use their own (short) base — give the user some breathing room
        nextCueAtRef.current = Date.now() + cueGapMs(a, intensity);
        return;
      }
    }
    // Compute elapsed time inside the current round so the sequencer can fire range cues on schedule
    const elapsedMs = Math.max(0, Date.now() - roundStartedAtRef.current);
    const c = seq.nextCue(elapsedMs);
    if (!c) return;
    setCue(c.text);
    const anim = animByCueRef.current[c.id] || null;
    setActiveAnim(anim);
    if (anim) setAnimKey((k) => k + 1); // restart flip-book from frame 0
    if (c.punches) {
      punchesRef.current += c.punches;
      setPunches(punchesRef.current);
    }
    playCueAudio(c);
    // Notify the admin-preview context (if mounted) so the admin can jump to this cue's editor.
    if (previewCtxRef.current?.onCueFired) {
      try { previewCtxRef.current.onCueFired(c); } catch { /* ignore */ }
    }
    // Also report which sequence (if any) is currently mid-firing — drives the second
    // "Edit current sequence" floating button. Null means we're in random/ratio territory.
    if (previewCtxRef.current?.onSequenceChange) {
      try { previewCtxRef.current.onSequenceChange(seq.getActiveSequence ? seq.getActiveSequence() : null); } catch { /* ignore */ }
    }
    // Schedule the NEXT cue based on THIS cue's complexity — a 4-punch combo gets more time
    // to land than a single jab. Honors cue.cue_seconds override if set, else derives from
    // cue.punches / cue.movement_count, all scaled by the current intensity tier.
    nextCueAtRef.current = Date.now() + cueGapMs(c, intensity);
  };

  // The single requestAnimationFrame engine — only runs after bootstrap.
  // Intentionally does NOT depend on `roundIdx` (read via roundIdxRef) — otherwise
  // every round transition would re-fire this effect, reset phaseEndsAtRef to
  // "now + 5000", and shrink rounds 2+ to a 5-second blip. The fix: keep the engine
  // long-lived; only the bootstrap dependency triggers (re)creation.
  useEffect(() => {
    if (!bootstrapped) return; // wait for cues + audio to load
    // Reset phase end so countdown starts now
    phaseEndsAtRef.current = Date.now() + 5000;
    lastFrameAtRef.current = Date.now();
    startedAtRef.current = Date.now();
    let rafId = null;
    const loop = () => {
      const now = Date.now();
      const dt = now - lastFrameAtRef.current;
      lastFrameAtRef.current = now;

      if (!pausedRef.current) {
        // Phase timer
        const remainingMs = phaseEndsAtRef.current - now;
        const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));
        setPhaseSecs(remainingSec);

        // Countdown ticks (5..1 with a beep on each new integer)
        if (phaseRef.current === "countdown" && remainingSec !== lastCountdownTickRef.current) {
          lastCountdownTickRef.current = remainingSec;
          if (remainingSec > 0 && remainingSec <= 5) {
            tick();
          }
        }

        // Accumulate active time during round
        if (phaseRef.current === "round") activeMsRef.current += dt;

        // Circuit mode — manage exercise breaks (enter/exit windows) before cue firing.
        if (phaseRef.current === "round" && exWindowsRef.current.length > 0) updateCircuit(now);

        // Cue firing during a round — `fireCue` schedules the NEXT cue based on the
        // complexity of the cue it just played, so the post-fire scheduling lives there.
        if (phaseRef.current === "round" && !inExerciseRef.current && now >= nextCueAtRef.current) {
          fireCue();
        }

        // Phase transitions
        if (remainingMs <= 0) {
          if (phaseRef.current === "countdown") {
            // Start round 1
            bell();
            phaseRef.current = "round";
            setPhase("round");
            phaseEndsAtRef.current = now + roundSecs * 1000;
            nextCueAtRef.current = now + FIRST_CUE_DELAY_MS; // 3s breather after the bell
            roundStartedAtRef.current = now;
            if (cueSequencerRef.current) cueSequencerRef.current.beginRound({ roundDurationSec: roundSecs });
            buildExerciseWindows();
            setCue("Box!");
          } else if (phaseRef.current === "round") {
            // End round
            bell();
            cancelSpeech();
            setActiveAnim(null);
            inExerciseRef.current = false;
            setExercise(null);
            const nextRound = roundIdxRef.current + 1;
            if (nextRound >= rounds) {
              // Done
              phaseRef.current = "done";
              setPhase("done");
              setCue("Workout complete");
              finishWorkout({ aborted: false });
              return; // stop loop
            }
            phaseRef.current = "rest";
            setPhase("rest");
            phaseEndsAtRef.current = now + restSecs * 1000;
            setCue("Rest");
            if (!mutedRef.current) speak("Rest", { rate: 0.95 });
          } else if (phaseRef.current === "rest") {
            // Next round
            bell();
            phaseRef.current = "round";
            const r = roundIdxRef.current + 1;
            roundIdxRef.current = r;
            setRoundIdx(r);
            setPhase("round");
            phaseEndsAtRef.current = now + roundSecs * 1000;
            nextCueAtRef.current = now + FIRST_CUE_DELAY_MS; // same 3s breather between rounds
            roundStartedAtRef.current = now;
            if (cueSequencerRef.current) cueSequencerRef.current.beginRound({ roundDurationSec: roundSecs });
            buildExerciseWindows();
            setCue(`Round ${r + 1}`);
            if (!mutedRef.current) speak(`Round ${r + 1}, box`, { rate: 1.0 });
          }
        }
      }
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => { if (rafId) cancelAnimationFrame(rafId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapped]);

  const finishWorkout = ({ aborted }) => {
    cancelSpeech();
    const activeSec = Math.round(activeMsRef.current / 1000);
    const elapsedSec = Math.round((Date.now() - startedAtRef.current) / 1000);
    const ri = roundIdxRef.current;
    const roundsCompleted = phaseRef.current === "done" ? rounds : ri + (phaseRef.current === "round" ? 1 : 0);
    const calories = estimateCalories({ activeSeconds: activeSec, intensityId: intensity });
    onComplete({
      aborted,
      mode,
      rounds, roundsCompleted: Math.min(roundsCompleted, rounds),
      elapsedSec, activeSec, punches: punchesRef.current, calories,
      config,
    });
  };

  const togglePause = () => {
    setPaused((p) => {
      const next = !p;
      pausedRef.current = next;
      if (next) {
        cancelSpeech();
      } else {
        // Resume — push the phase end forward by however long we paused doesn't matter
        // because we update phaseEndsAtRef to be relative to "now + remaining" when paused.
        // Simpler: just advance phaseEndsAtRef by the time since lastFrame.
        lastFrameAtRef.current = Date.now();
      }
      return next;
    });
  };
  // Adjust phaseEndsAt when pausing so the timer "freezes" cleanly
  useEffect(() => {
    if (paused) {
      const remainingMs = phaseEndsAtRef.current - Date.now();
      // Stash remaining + freeze
      phaseEndsAtRef.current = Number.MAX_SAFE_INTEGER; // freeze timer to far future
      // Save remaining for resume
      pausedRemainingRef.current = remainingMs;
    } else if (pausedRemainingRef.current != null) {
      // Resume: set phase end based on remaining ms
      phaseEndsAtRef.current = Date.now() + pausedRemainingRef.current;
      pausedRemainingRef.current = null;
    }
  }, [paused]);

  const skipPhase = () => {
    phaseEndsAtRef.current = Date.now() - 1; // force transition next frame
  };

  const stop = () => {
    if (!window.confirm("End the workout now?")) return;
    finishWorkout({ aborted: true });
  };

  return {
    bootstrapped, bootError,
    phase, phaseSecs, roundIdx, paused, muted, cue, punches,
    activeAnim, animKey, exercise,
    activeMsRef,
    setMuted, togglePause, skipPhase, stop,
  };
}
